import type { Brand } from './brand'

/* Opaque, per-entity identifiers. Branding prevents passing a PhaseId where a
   GoalId is expected — a class of bug that is otherwise invisible because both
   are strings. */
export type ProjectId = Brand<string, 'ProjectId'>
export type PhaseId = Brand<string, 'PhaseId'>
export type GoalId = Brand<string, 'GoalId'>
export type MeasurementSessionId = Brand<string, 'MeasurementSessionId'>
export type PhotoId = Brand<string, 'PhotoId'>
export type SentinelLiftId = Brand<string, 'SentinelLiftId'>
export type StrengthEntryId = Brand<string, 'StrengthEntryId'>
export type WeeklyReviewId = Brand<string, 'WeeklyReviewId'>
export type OpinionId = Brand<string, 'OpinionId'>
export type AttachmentId = Brand<string, 'AttachmentId'>
export type BlobId = Brand<string, 'BlobId'>

/* Version 2, schema v3 (D-090, D-092). `DomainGoalId` is deliberately not
   `GoalId`: a domain goal and the body-composition target are different records
   with different lifecycles, and branding is what stops one being passed where
   the other is expected. The bare word "goal" is never used alone. */
export type DomainGoalId = Brand<string, 'DomainGoalId'>
export type NutritionPlanId = Brand<string, 'NutritionPlanId'>
export type RoutineAnchorId = Brand<string, 'RoutineAnchorId'>
export type WearableImportId = Brand<string, 'WearableImportId'>
export type AiProposalId = Brand<string, 'AiProposalId'>

/* Schema v4 (D-105). `ScheduledSessionId` identifies a *recurring* session on a
   domain goal's schedule; `TrainingWeekId` will identify a materialised week.
   They are branded apart from each other for the same reason `DomainGoalId` is
   branded apart from `GoalId`: the plan layer and the execution layer are
   different records with different lifecycles, and passing one where the other
   belongs is how an instance action writes back to the schedule. */
export type ScheduledSessionId = Brand<string, 'ScheduledSessionId'>
export type TrainingWeekId = Brand<string, 'TrainingWeekId'>

/** `AthleteProfile` and `Settings` are single-record stores (D-013, D-027). */
export const SINGLETON_KEY = 'singleton'
export type SingletonKey = typeof SINGLETON_KEY

/**
 * Generate an opaque identifier.
 *
 * `crypto.randomUUID` requires a secure context and is available on the
 * iOS 17+ baseline (D-032). The fallback keeps non-secure development contexts
 * and older test environments working; it is not used in production.
 */
export function newId<T extends string>(): Brand<string, T> {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID() as Brand<string, T>
  }
  return fallbackId() as Brand<string, T>
}

function fallbackId(): string {
  const bytes = new Uint8Array(16)
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes)
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256)
    }
  }
  // Set the version and variant bits so the result is a well-formed UUIDv4.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
